import { dateHelpers } from "@runn/calculations"
import { keyBy } from "lodash-es"
import React from "react"
import { graphql, useFragment, useLazyLoadQuery } from "react-relay"

import { ProjectNoteQuery } from "./__generated__/ProjectNoteQuery.graphql"
import { ProjectNote_note$key } from "./__generated__/ProjectNote_note.graphql"

import { formatName } from "~/helpers/person"

import { noteDeleteRelay, noteUpdateRelay } from "~/mutations/Note"

import { usePermissions } from "~/Permissions/usePermissions"

import Note from "../../../forms/Notes/Note"

type NoteProps = {
  note: ProjectNote_note$key
  style?: {
    container?: React.CSSProperties
    note?: React.CSSProperties
    createdBy?: React.CSSProperties
  }
  timeShown?: boolean
}

const noteFragment = graphql`
  fragment ProjectNote_note on notes {
    id
    note
    created_by
    created_at
    updated_at
    creator_email
    user_id
    project {
      id
      isTemplate: is_template
    }
  }
`

const noteQuery = graphql`
  query ProjectNoteQuery {
    current_account {
      id
      user_accounts {
        id
        user {
          id
          first_name
          last_name
          account_id
          email
        }
      }
    }
  }
`

const ProjectNote = (props: NoteProps) => {
  const { style, timeShown } = props
  const { current_account } = useLazyLoadQuery<ProjectNoteQuery>(noteQuery, {})

  const {
    id,
    note,
    created_by,
    creator_email,
    user_id,
    created_at,
    updated_at,
    project,
  } = useFragment(noteFragment, props.note)

  const users = keyBy(
    current_account.user_accounts.map((ua) => ua.user),
    "id",
  )
  const user = users[user_id]

  const { can, subject } = usePermissions()
  const canEditNote = can(
    "edit",
    subject("ProjectNote", {
      user: {
        id: user?.id,
      },
      project,
    }),
  )
  const canDeleteNote = can(
    "delete",
    subject("ProjectNote", {
      user: { id: user?.id },
      project,
    }),
  )

  const updateNote = async (noteVal: string) => {
    await noteUpdateRelay({
      note: {
        id,
        note: noteVal,
      },
    })
  }

  const deleteNote = (noteId) => {
    void noteDeleteRelay({ id: noteId })
  }

  const userName = user
    ? formatName(user.first_name, user.last_name)
    : created_by || "Deleted user"
  const createdBy = creator_email ? `${userName} (${creator_email})` : userName
  const updatedAt = dateHelpers.formatToRunnDate(
    new Date(updated_at || created_at),
  )

  // Checking if the updatedAt is greater than the following date
  // because user reference was only added to notes on this date
  // https://github.com/Runn-Fast/runn/releases/tag/v2.63.0 (#11232)
  const isUserDeleted =
    !user &&
    updatedAt > "20230411" &&
    created_by !== "API" &&
    Boolean(creator_email)

  return (
    <Note
      noteId={id}
      note={note}
      createdDate={created_at}
      modifiedDate={updated_at}
      createdBy={createdBy}
      onUpdate={updateNote}
      onDelete={deleteNote}
      editable={canEditNote}
      isUserDeleted={isUserDeleted}
      deletable={canDeleteNote}
      style={style}
      timeShown={timeShown}
    />
  )
}

export default ProjectNote
